import * as THREE from 'three'
import { COLORS } from '../constants/design'

/** Procedural canvas textures + a shared material library so every entity reuses
 * the same tuned materials (premium "shared materials" requirement, fewer draw states). */

const col = (hex: string) => new THREE.Color(hex)

function makeCanvas(size: number): { cv: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const cv = document.createElement('canvas')
  cv.width = cv.height = size
  return { cv, ctx: cv.getContext('2d')! }
}

/** Deterministic PRNG (mulberry32). The procedural textures previously used unseeded Math.random(),
 * so the wall/floor/decor surfaces reshuffled every page load — visually equivalent, but it flakes any
 * pixel-exact visual-regression baseline. Each generator draws from its own seeded stream (distinct
 * constant) so the output is identical every load and independent of call order. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Porous sponge surface — light base with darker holes; used as bump + subtle map. */
function spongeTexture(): THREE.CanvasTexture {
  const rnd = mulberry32(0x5b0c6e01)
  const { cv, ctx } = makeCanvas(256)
  ctx.fillStyle = '#dff3ff'
  ctx.fillRect(0, 0, 256, 256)
  for (let i = 0; i < 220; i++) {
    const x = rnd() * 256
    const y = rnd() * 256
    const r = 2 + rnd() * 8
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(10,40,70,${0.2 + rnd() * 0.45})`
    ctx.fill()
  }
  const t = new THREE.CanvasTexture(cv)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.repeat.set(2, 2)
  t.anisotropy = 4
  return t
}

/** Sandy seabed — warm base with rippled grain. Returns a color map. */
function sandTexture(): THREE.CanvasTexture {
  const rnd = mulberry32(0x5a4d0042)
  const { cv, ctx } = makeCanvas(512)
  const g = ctx.createLinearGradient(0, 0, 0, 512)
  g.addColorStop(0, '#f4e8c1')
  g.addColorStop(1, '#e7d39a')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 512, 512)
  // ripple bands
  ctx.globalAlpha = 0.18
  for (let y = 0; y < 512; y += 6) {
    const off = Math.sin(y * 0.08) * 10
    ctx.strokeStyle = y % 12 === 0 ? '#c9b06a' : '#fff6dd'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(0, y)
    for (let x = 0; x <= 512; x += 16) ctx.lineTo(x, y + Math.sin((x + off) * 0.05) * 3)
    ctx.stroke()
  }
  // grain speckle
  ctx.globalAlpha = 0.5
  for (let i = 0; i < 1400; i++) {
    ctx.fillStyle = rnd() > 0.5 ? '#bfa460' : '#fffbe9'
    ctx.fillRect(rnd() * 512, rnd() * 512, 1.5, 1.5)
  }
  ctx.globalAlpha = 1
  const t = new THREE.CanvasTexture(cv)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.repeat.set(8, 8)
  t.anisotropy = 8
  return t
}

/** Mottled coral/rock surface for the maze walls. */
function coralTexture(): THREE.CanvasTexture {
  const rnd = mulberry32(0xc0a17d05)
  const { cv, ctx } = makeCanvas(256)
  ctx.fillStyle = '#1f93a1'
  ctx.fillRect(0, 0, 256, 256)
  const blobs = ['#2bb3b0', '#178799', '#37c0a8', '#1a8f9e']
  for (let i = 0; i < 90; i++) {
    ctx.beginPath()
    ctx.arc(rnd() * 256, rnd() * 256, 4 + rnd() * 16, 0, Math.PI * 2)
    ctx.fillStyle = blobs[(rnd() * blobs.length) | 0]
    ctx.globalAlpha = 0.5
    ctx.fill()
  }
  // pin-prick coral pores
  ctx.globalAlpha = 0.5
  for (let i = 0; i < 260; i++) {
    ctx.fillStyle = '#0b3d54'
    ctx.beginPath()
    ctx.arc(rnd() * 256, rnd() * 256, 0.8 + rnd() * 1.6, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1
  const t = new THREE.CanvasTexture(cv)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.repeat.set(1, 1)
  return t
}

/** Soft animated caustic light pattern (used by a projecting effect / floor overlay). */
export function causticTexture(size = 256): THREE.CanvasTexture {
  const rnd = mulberry32(0xca05710c)
  const { cv, ctx } = makeCanvas(size)
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, size, size)
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 40; i++) {
    const x = rnd() * size
    const y = rnd() * size
    const r = 10 + rnd() * 40
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r)
    grad.addColorStop(0, 'rgba(190,245,255,0.5)')
    grad.addColorStop(1, 'rgba(190,245,255,0)')
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  const t = new THREE.CanvasTexture(cv)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  return t
}

export interface MaterialLibrary {
  spongeBody: THREE.MeshStandardMaterial
  spongeFace: THREE.MeshStandardMaterial
  eyeWhite: THREE.MeshStandardMaterial
  eyeIris: THREE.MeshStandardMaterial
  mouth: THREE.MeshStandardMaterial
  sand: THREE.MeshStandardMaterial
  coralWall: THREE.MeshStandardMaterial
  wallTrim: THREE.MeshStandardMaterial
  jellyBell: THREE.MeshPhysicalMaterial
  jellyTentacle: THREE.MeshStandardMaterial
  pearl: THREE.MeshPhysicalMaterial
  shell: THREE.MeshStandardMaterial
  pattyBun: THREE.MeshStandardMaterial
  pattyMeat: THREE.MeshStandardMaterial
  pattyLettuce: THREE.MeshStandardMaterial
  coralProp: THREE.MeshStandardMaterial[]
  seaweed: THREE.MeshStandardMaterial
}

let lib: MaterialLibrary | null = null

export function materials(): MaterialLibrary {
  if (lib) return lib
  const sponge = spongeTexture()
  const sand = sandTexture()
  const coral = coralTexture()

  lib = {
    spongeBody: new THREE.MeshStandardMaterial({
      color: col('#3FC1F0'),
      map: sponge,
      bumpMap: sponge,
      bumpScale: 0.06,
      roughness: 0.78,
      metalness: 0.02,
    }),
    spongeFace: new THREE.MeshStandardMaterial({ color: col('#bfeefe'), roughness: 0.7 }),
    eyeWhite: new THREE.MeshStandardMaterial({ color: col('#ffffff'), roughness: 0.25 }),
    eyeIris: new THREE.MeshStandardMaterial({ color: col('#15243a'), roughness: 0.2 }),
    mouth: new THREE.MeshStandardMaterial({ color: col(COLORS.SECONDARY), roughness: 0.5 }),
    sand: new THREE.MeshStandardMaterial({ color: col('#ffffff'), map: sand, roughness: 0.95, metalness: 0 }),
    // Walls are tinted per-level via InstancedMesh instanceColor. The coral texture is used
    // for surface RELIEF only (bumpMap) — NOT as a colour map — so a saturated theme hue
    // (red / purple / amber) reads true instead of being muddied by the texture's albedo.
    coralWall: new THREE.MeshStandardMaterial({
      color: col('#ffffff'),
      bumpMap: coral,
      bumpScale: 0.16,
      roughness: 0.82,
      metalness: 0.04,
    }),
    wallTrim: new THREE.MeshStandardMaterial({
      color: col(COLORS.FOAM),
      emissive: col('#2aa7c4'),
      emissiveIntensity: 0.55,
      roughness: 0.4,
    }),
    // NB: NO `transmission` here. MeshPhysicalMaterial transmission forces the renderer to re-render
    // the WHOLE scene to a transmission buffer EVERY frame (one extra full scene pass + ~100 extra
    // draw calls) — measured at ~80fps of cost for a few translucent jellies. The glassy look is
    // preserved cheaply with plain alpha (transparent + opacity, set per-jelly at runtime) plus
    // iridescence/clearcoat sheen and emissive glow, none of which need the extra pass.
    jellyBell: new THREE.MeshPhysicalMaterial({
      color: col(COLORS.ACCENT),
      roughness: 0.18,
      metalness: 0,
      iridescence: 0.6,
      iridescenceIOR: 1.3,
      transparent: true,
      opacity: 0.92,
      clearcoat: 0.6,
      clearcoatRoughness: 0.3,
      emissive: col(COLORS.ACCENT),
      emissiveIntensity: 0.25,
      side: THREE.DoubleSide,
    }),
    jellyTentacle: new THREE.MeshStandardMaterial({
      color: col('#ff9ad1'),
      roughness: 0.4,
      transparent: true,
      opacity: 0.8,
      emissive: col(COLORS.ACCENT),
      emissiveIntensity: 0.2,
    }),
    pearl: new THREE.MeshPhysicalMaterial({
      color: col('#d5f4ff'),
      roughness: 0.055,
      metalness: 0.65,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
      iridescence: 1,
      iridescenceIOR: 1.4,
      emissive: col('#cfe9ff'),
      emissiveIntensity: 0.035,
      envMapIntensity: 1.25,
    }),
    shell: new THREE.MeshStandardMaterial({
      color: col('#ffd9a8'),
      roughness: 0.5,
      metalness: 0.05,
      emissive: col(COLORS.CORAL),
      emissiveIntensity: 0.12,
    }),
    pattyBun: new THREE.MeshStandardMaterial({ color: col('#e3a64e'), roughness: 0.7 }),
    pattyMeat: new THREE.MeshStandardMaterial({ color: col('#7a3b1e'), roughness: 0.8 }),
    pattyLettuce: new THREE.MeshStandardMaterial({ color: col('#79c267'), roughness: 0.6 }),
    coralProp: [
      new THREE.MeshStandardMaterial({ color: col(COLORS.CORAL), roughness: 0.6 }),
      new THREE.MeshStandardMaterial({ color: col(COLORS.ACCENT), roughness: 0.6 }),
      new THREE.MeshStandardMaterial({ color: col(COLORS.PRIMARY), roughness: 0.6 }),
      new THREE.MeshStandardMaterial({ color: col('#9b6cff'), roughness: 0.6 }),
    ],
    seaweed: new THREE.MeshStandardMaterial({
      color: col('#2fa86a'),
      roughness: 0.6,
      side: THREE.DoubleSide,
    }),
  }

  return lib
}

/** Per-jellyfish tint variants over the shared bell material (cloned, palette-coherent). */
export const JELLY_TINTS: Record<string, string> = {
  chaser: COLORS.ACCENT, // pink
  ambusher: '#7ad7ff', // cyan
  patroller: COLORS.PRIMARY, // yellow
  wanderer: '#b18cff', // violet
}
